import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Heart, Moon, Scale, Smile, Meh, Frown } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { type IDailyHealthRecord } from '@/hooks/useAppState';
import { useDailyHealth } from '@/hooks/useDailyHealth';
import { formatLocalDate } from '@/lib/utils';
import HealthFourRings from './HealthFourRings';

interface HealthCalendarSheetProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const WEEK_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

function getMoodIcon(mood?: 'great' | 'normal' | 'poor') {
  if (mood === 'great') return Smile;
  if (mood === 'poor') return Frown;
  return Meh;
}

function getMoodColor(mood?: 'great' | 'normal' | 'poor') {
  if (mood === 'great') return 'text-success';
  if (mood === 'poor') return 'text-destructive';
  return 'text-warning';
}

export default function HealthCalendarSheet({ trigger, open, onOpenChange }: HealthCalendarSheetProps) {
  const navigate = useNavigate();
  const { records, getRecordByDate, getCheckInCount, isFullCheckIn } = useDailyHealth();

  const today = new Date();
  const todayStr = formatLocalDate(today);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // 生成当月日历格子
  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const lastDay = new Date(viewYear, viewMonth + 1, 0);
    const startWeekday = firstDay.getDay(); // 0=周日
    const daysInMonth = lastDay.getDate();

    const cells: { date: string; day: number; isCurrentMonth: boolean; isToday: boolean; record: IDailyHealthRecord | null; count: number; full: boolean }[] = [];

    // 上个月填充
    const prevMonthLast = new Date(viewYear, viewMonth, 0).getDate();
    for (let i = startWeekday - 1; i >= 0; i--) {
      const d = prevMonthLast - i;
      const dateStr = formatLocalDate(new Date(viewYear, viewMonth - 1, d));
      const rec = getRecordByDate(dateStr);
      cells.push({
        date: dateStr,
        day: d,
        isCurrentMonth: false,
        isToday: false,
        record: rec,
        count: getCheckInCount(rec),
        full: isFullCheckIn(rec),
      });
    }

    // 当月
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = formatLocalDate(new Date(viewYear, viewMonth, d));
      const rec = getRecordByDate(dateStr);
      cells.push({
        date: dateStr,
        day: d,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        record: rec,
        count: getCheckInCount(rec),
        full: isFullCheckIn(rec),
      });
    }

    // 下个月填充到 42 格（6 周）
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const dateStr = formatLocalDate(new Date(viewYear, viewMonth + 1, d));
      const rec = getRecordByDate(dateStr);
      cells.push({
        date: dateStr,
        day: d,
        isCurrentMonth: false,
        isToday: false,
        record: rec,
        count: getCheckInCount(rec),
        full: isFullCheckIn(rec),
      });
    }

    return cells;
  }, [viewYear, viewMonth, getRecordByDate, getCheckInCount, isFullCheckIn, todayStr]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const selectedRecord = selectedDate ? getRecordByDate(selectedDate) : null;
  const selectedCount = selectedRecord ? getCheckInCount(selectedRecord) : 0;

  const moodLabel = (() => {
    if (!selectedRecord?.mood) return '';
    if (selectedRecord.mood === 'great') return '很好';
    if (selectedRecord.mood === 'poor') return '疲惫';
    return '一般';
  })();

  const handleEditDay = (dateStr: string) => {
    // 关闭日历，打开打卡 sheet：通过 navigate 到首页 + 触发不太方便
    // 简化：直接关闭日历，用户回首页点胶囊录入
    if (onOpenChange) onOpenChange(false);
    // 如果是今天，留在首页；如果是其他天，暂不支持历史补录编辑（需求说"可补录"，但 sheet 是按 today 设计的）
    // 这里先简单关闭，留待后续增强
    navigate('/');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent side="bottom" className="rounded-t-3xl px-5 pb-8 pt-4 max-h-[90vh] overflow-y-auto">
        <SheetHeader className="mb-3">
          <SheetTitle className="text-base text-center">健康打卡日历</SheetTitle>
        </SheetHeader>

        {/* 月份切换 */}
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center active:scale-95 transition-transform"
          >
            <ChevronLeft className="w-4 h-4 text-foreground" />
          </button>
          <span className="text-sm font-bold text-foreground">
            {viewYear} 年 {viewMonth + 1} 月
          </span>
          <button
            type="button"
            onClick={handleNextMonth}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center active:scale-95 transition-transform"
          >
            <ChevronRight className="w-4 h-4 text-foreground" />
          </button>
        </div>

        {/* 星期头 */}
        <div className="grid grid-cols-7 mb-1">
          {WEEK_LABELS.map(w => (
            <div key={w} className="text-center text-[10px] text-muted-foreground py-1">
              {w}
            </div>
          ))}
        </div>

        {/* 日历格子 */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {calendarDays.map(cell => {
            const selected = selectedDate === cell.date;
            return (
              <button
                key={cell.date}
                type="button"
                onClick={() => setSelectedDate(cell.date)}
                className={`relative aspect-square rounded-lg flex items-center justify-center text-xs transition-all ${
                  !cell.isCurrentMonth
                    ? 'text-muted-foreground/30'
                    : cell.isToday
                      ? 'bg-primary text-white font-bold'
                      : selected
                        ? 'bg-primary/15 text-primary font-semibold'
                        : 'text-foreground hover:bg-muted'
                }`}
              >
                <span>{cell.day}</span>
                {/* 四环小圆点指示：根据 count 显示小点 */}
                {cell.count > 0 && cell.isCurrentMonth && !cell.isToday && (
                  <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-[2px]">
                    {cell.full ? (
                      <span className="w-1 h-1 rounded-full bg-primary" />
                    ) : (
                      <>
                        {Array.from({ length: cell.count }).map((_, i) => (
                          <span key={i} className="w-0.5 h-0.5 rounded-full bg-primary/60" />
                        ))}
                      </>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* 图例 */}
        <div className="flex items-center justify-center gap-3 mb-4 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            完整打卡
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-primary/60" />
            部分填写
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-border" />
            未打卡
          </span>
        </div>

        {/* 选中日期详情 */}
        {selectedDate && (
          <div className="border-t border-border/40 pt-4">
            <div className="flex items-start gap-3 mb-3">
              <HealthFourRings record={selectedRecord} size={70} strokeWidth={4} gap={3} />
              <div className="flex-1">
                <p className="text-sm font-bold text-foreground">
                  {selectedDate}
                  {selectedDate === todayStr && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary">今天</span>}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {selectedCount === 0 ? '未打卡' : `已填写 ${selectedCount}/4 项`}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-card border border-border/40">
                <Heart className="w-3.5 h-3.5 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground">静息心率</p>
                  <p className="text-xs font-semibold text-foreground truncate">
                    {selectedRecord?.restingHeartRate !== undefined
                      ? `${selectedRecord.restingHeartRate} bpm`
                      : '未填写'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-card border border-border/40">
                <Moon className="w-3.5 h-3.5 text-info shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground">睡眠时长</p>
                  <p className="text-xs font-semibold text-foreground truncate">
                    {selectedRecord?.sleepHours !== undefined
                      ? `${selectedRecord.sleepHours} h`
                      : '未填写'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-card border border-border/40">
                <Scale className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground">体重</p>
                  <p className="text-xs font-semibold text-foreground truncate">
                    {selectedRecord?.weight !== undefined
                      ? `${selectedRecord.weight} kg`
                      : '未填写'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-card border border-border/40">
                {(() => {
                  const MoodIcon = getMoodIcon(selectedRecord?.mood);
                  return <MoodIcon className={`w-3.5 h-3.5 shrink-0 ${getMoodColor(selectedRecord?.mood)}`} />;
                })()}
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground">心情</p>
                  <p className="text-xs font-semibold text-foreground truncate">
                    {selectedRecord?.mood ? moodLabel : '未填写'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!selectedDate && (
          <p className="text-[10px] text-muted-foreground text-center">
            点击日期查看当天打卡详情
          </p>
        )}
      </SheetContent>
    </Sheet>
  );
}
